/**
 * The front desk's reads and sends (Reception C5, C8, C34; R12, B23).
 *
 * TODAY OFFLINE. The list is kept per person and per building on this device,
 * exactly as the server sent it (a lobby desk's rows hold name, company and
 * status, so that is all that is ever kept: B24). When the network fails the
 * read answers from it, marked `cached`, with the time it was fetched.
 *
 * CHECK-IN AND CHECK-OUT go through the technician's queue: on the device first,
 * off it only when the server says 2xx. A refusal the person is looking at right
 * now is shown to them and leaves no copy behind; a refusal of something sent
 * later from the queue stays in the blocked list, where the queue line shows it.
 */
import { useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, getSignedInUserId, httpStatus } from '@/utils/api';
import { captureAndSend, discardBlocked, getBlocked, takeRejected } from '@/services/pendingQueue';
import { usePendingQueue } from '@/components/Common/usePendingQueue';

export interface DeskDevice { id: string; make_model: string; serial: string; matched: boolean; mismatch: boolean }
export interface DeskPerson { id: string; name: string; phone: string | null; department?: string | null }
export interface DeskWaiting { since: string; minutes: number; stage: number; next_stage_at: string }

/** A visit as the desk reads it. A lobby desk gets the first four fields only. */
export interface DeskRow {
  id: string;
  name: string;
  host_company: string | null;
  status: string;
  visitor_id?: string;
  phone?: string | null;
  email?: string | null;
  visitor_company?: string | null;
  vehicle_number?: string | null;
  purpose?: string | null;
  type_name?: string | null;
  is_vip?: boolean;
  issues_badge?: boolean;
  captures_photo?: boolean;
  declares_equipment?: boolean;
  needs_documents?: boolean;
  needs_nda?: boolean;
  needs_approval?: boolean;
  expected_arrival?: string | null;
  time_known?: boolean | null;
  checked_in_at?: string | null;
  checked_out_at?: string | null;
  due_out?: string | null;
  overdue?: boolean;
  badge_number?: string | null;
  is_minor?: boolean;
  from_paper?: boolean;
  checkout_how?: string | null;
  group_id?: string | null;
  card?: { issue_id: string; label: string; not_returned: boolean } | null;
  devices?: DeskDevice[];
  host?: DeskPerson | null;
  backup?: DeskPerson | null;
  waiting?: DeskWaiting | null;
  host_answer?: string | null;
  admit_reason?: string | null;
  email_not_delivered?: boolean;
  /** Only on GET /reception/visit-by-qr. */
  day_problem?: string | null;
  /** Only on GET /reception/approvals. */
  approver?: string | null;
  can_approve?: boolean;
  why_not?: string | null;
  zone?: string;
}

export interface DeskCounts {
  expected: number; waiting: number; on_site: number; overdue: number; vip: number;
  parcels: number; passes_due: number; passes_overdue: number; cards_out: number;
}

export interface DeskToday {
  building: { id: string; name: string; zone: string };
  server_time: string;
  day: string;
  lobby: boolean;
  escalation_minutes: number;
  counts: DeskCounts;
  expected: DeskRow[];
  waiting: DeskRow[];
  on_site: DeskRow[];
  checked_out: DeskRow[];
}

export interface DeskTodayRead { today: DeskToday; at: number; cached: boolean }

export interface DeskVisitorType {
  id: string; name: string; requires_approval: boolean; requires_nda: boolean; captures_photo: boolean;
  host_confirms: boolean; issues_badge: boolean; is_vip: boolean; declares_equipment: boolean;
  needs_documents: boolean; max_stay_minutes: number | null;
}

export interface DeskHost { id: string; name: string; department: string | null; company: string; same_name: boolean }

/** Every list that shows a visit, and not /reception/me (nothing here changes it). */
export const DESK_LISTS = {
  queryKey: ['reception'],
  predicate: (q: { queryKey: readonly unknown[] }) => q.queryKey[1] !== 'me',
};

/** No answer at all, or the host in front of the server saying it is down. */
export function isNoSignal(err: unknown): boolean {
  const status = httpStatus(err);
  return status === 0 || status === 502 || status === 503 || status === 504;
}

// ---------------------------------------------------------------------------
// Today, kept on this device per person
// ---------------------------------------------------------------------------

const todayKey = (buildingId: string) => `reception-today:${getSignedInUserId() ?? 'none'}:${buildingId}`;
const stampKey = (buildingId: string) => `reception-today-at:${getSignedInUserId() ?? 'none'}:${buildingId}`;
const stampListeners = new Set<() => void>();

function saveToday(buildingId: string, read: { today: DeskToday; at: number }): void {
  try {
    localStorage.setItem(todayKey(buildingId), JSON.stringify(read));
    localStorage.setItem(stampKey(buildingId), `${read.at}|${read.today.building.zone}`);
  } catch { /* full: this page still has it; only the offline copy is older */ }
  stampListeners.forEach((l) => l());
}

export function savedToday(buildingId: string): { today: DeskToday; at: number } | null {
  try {
    const raw = localStorage.getItem(todayKey(buildingId));
    return raw ? (JSON.parse(raw) as { today: DeskToday; at: number }) : null;
  } catch {
    return null;
  }
}

/** When this device last heard from the server about this building, and the
 *  building's clock to say it in. The top bar reads this rather than asking the
 *  server: GET /reception/today does the desk's timed work, so it is not called
 *  from every page. */
export function useDeskDataAge(buildingId: string | undefined): { at: number; zone: string } | null {
  const raw = useSyncExternalStore(
    (cb) => { stampListeners.add(cb); return () => { stampListeners.delete(cb); }; },
    () => {
      if (!buildingId) return '';
      try { return localStorage.getItem(stampKey(buildingId)) ?? ''; } catch { return ''; }
    },
  );
  return useMemo(() => {
    if (!raw) return null;
    const [at, zone] = raw.split('|');
    return { at: Number(at), zone: zone ?? '' };
  }, [raw]);
}

export function useDeskToday(buildingId: string | undefined, { poll = false }: { poll?: boolean } = {}) {
  return useQuery<DeskTodayRead>({
    queryKey: ['reception', 'today', buildingId],
    queryFn: async () => {
      const id = buildingId as string;
      try {
        const today = await apiGet<DeskToday>(`/reception/today?building_id=${id}`);
        const at = Date.now();
        saveToday(id, { today, at });
        return { today, at, cached: false };
      } catch (e) {
        const kept = isNoSignal(e) ? savedToday(id) : null;
        if (kept) return { ...kept, cached: true };
        throw e;
      }
    },
    enabled: !!buildingId,
    // Offline: fail at once and answer from this device, never pause.
    networkMode: 'always',
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchInterval: poll ? 30_000 : false,
  });
}

export function useDeskTypes() {
  return useQuery({
    queryKey: ['reception', 'visitor-types'],
    queryFn: () => apiGet<DeskVisitorType[]>('/reception/visitor-types'),
    staleTime: 60_000,
  });
}

/** The visit's type's rules, in words a new starter can read. */
export function typeRules(t: DeskVisitorType): string[] {
  return [
    t.host_confirms ? 'Host confirms on arrival' : 'No host confirmation',
    t.issues_badge ? 'Badge at the desk' : 'No badge',
    t.captures_photo ? 'Photo on badge' : 'No photo',
    ...(t.declares_equipment ? ['Laptop or tools declared at entry'] : []),
    ...(t.needs_documents ? ['Documents checked for expiry'] : []),
    ...(t.max_stay_minutes ? [`Leaves within ${t.max_stay_minutes} min`] : []),
  ];
}

/** Why this type can't walk in at the desk, or null. The server's own reasoning. */
export function walkInRefusal(t: DeskVisitorType): string | null {
  if (t.is_vip) return "VIPs are approved before the day. Ask the host to come down, or pre-register so the host's manager can approve.";
  const needs = [t.requires_nda ? 'a signed NDA' : null, t.requires_approval ? 'approval' : null].filter(Boolean);
  return needs.length ? `${t.name} visitors need ${needs.join(' and ')} before they come in. Pre-register the visit instead.` : null;
}

// ---------------------------------------------------------------------------
// Check-in and check-out, queued
// ---------------------------------------------------------------------------

export type DeskSend =
  | { outcome: 'sent'; row: DeskRow | null }
  | { outcome: 'saved' }
  | { outcome: 'refused'; status: number; message: string };

async function send(label: string, endpoint: string, body: Record<string, unknown>): Promise<DeskSend> {
  let r;
  try {
    r = await captureAndSend({ label, endpoint, method: 'POST', body });
  } catch (e) {
    return { outcome: 'refused', status: 0, message: e instanceof Error ? e.message : 'This device could not save that.' };
  }
  if (r.sent) return { outcome: 'sent', row: (r.data as DeskRow | undefined) ?? null };
  if (!r.permanent) return { outcome: 'saved' };
  const item = getBlocked().filter((b) => b.endpoint === endpoint).pop();
  // A server that kept failing is not an answer: it stays where it can be seen.
  if (item && !item.heldBack) {
    discardBlocked(item.id);
    takeRejected(getSignedInUserId());
  }
  return { outcome: 'refused', status: item?.status ?? 0, message: r.error ?? 'That was refused.' };
}

const checkInPath = (id: string) => `/reception/visits/${id}/check-in`;
const checkOutPath = (id: string) => `/reception/visits/${id}/check-out`;

export function sendCheckIn(row: Pick<DeskRow, 'id' | 'name'>, fields: Record<string, unknown> = {}): Promise<DeskSend> {
  return send(`Check-in — ${row.name}`, checkInPath(row.id), { client_at: new Date().toISOString(), ...fields });
}

export function sendCheckOut(row: Pick<DeskRow, 'id' | 'name'>, answers: { card_returned?: boolean; devices_match?: boolean } = {}): Promise<DeskSend> {
  return send(`Check-out — ${row.name}`, checkOutPath(row.id), { ...answers, client_at: new Date().toISOString() });
}

export interface DeskQueued { kind: 'check-in' | 'check-out'; refused: string | null }

/** What this device still holds for each visit, by visit id. */
export function useDeskQueued(): Map<string, DeskQueued> {
  const { waiting, blocked } = usePendingQueue();
  return useMemo(() => {
    const out = new Map<string, DeskQueued>();
    const read = (endpoint: string, refused: string | null) => {
      const m = endpoint.match(/^\/reception\/visits\/([^/]+)\/(check-in|check-out)$/);
      if (m?.[1]) out.set(m[1], { kind: m[2] as DeskQueued['kind'], refused });
    };
    waiting.forEach((i) => read(i.endpoint, null));
    blocked.forEach((i) => read(i.endpoint, i.lastError ?? 'Not accepted'));
    return out;
  }, [waiting, blocked]);
}
